// src/components/admin/Products.jsx
import * as React from 'react';
import {
  Paper, Box, Typography, Divider, List, ListItemButton, ListItemText,
  IconButton, Tooltip, TextField, Button, Alert, Switch, FormControlLabel,
  InputAdornment, Stack, Collapse, Chip, MenuItem
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { api } from '../../lib/api';

const BLUE = '#0466c8';
const BLUE_HOVER = '#004890';
const blueBtn = { bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER } };
const subtleCell = {
  bgcolor: '#f6f6f6',
  border: '1px dashed #e0e0e0',
  borderRadius: 2,
};
const textFieldSx = {
  '& label.Mui-focused': { color: BLUE },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#ccc' },
    '&:hover fieldset': { borderColor: BLUE },
    '&.Mui-focused fieldset': { borderColor: BLUE },
  },
};
const Muted = (props) => <Typography sx={{ color: 'text.secondary', fontSize: 13.5 }} {...props} />;

// enum côté UI (aligné avec le backend)
const MODEL_TYPES = [
  { value: 'phone', label: 'Téléphone' },
  { value: 'accessories', label: 'Accessoires' },
  { value: 'tablet', label: 'Tablette' },
];

export default function AdminProducts() {
  /** ===================== STATE ===================== */
  const [expanded, setExpanded] = React.useState(null); // null | 'brands' | 'models' | 'components'
  const [err, setErr] = React.useState('');
  const [ok, setOk] = React.useState('');

  // data
  const [brands, setBrands] = React.useState([]);
  const [models, setModels] = React.useState([]);
  const [components, setComponents] = React.useState([]);

  // selections
  const [brandSel, setBrandSel] = React.useState(null);
  const [modelSel, setModelSel] = React.useState(null);

  // inline create/edit toggles
  const [addingBrand, setAddingBrand] = React.useState(false);
  const [addingModel, setAddingModel] = React.useState(false);
  const [addingComponent, setAddingComponent] = React.useState(false);

  const [modelSearch, setModelSearch] = React.useState('');

  // forms
  const [brandForm, setBrandForm] = React.useState({
    id: null,
    brand: '',
    hasPricingInfo: false,
    pricingPrice: '',
    pricingDescription: '',
    isActive: true, // ✅ marque active par défaut
  });
  const [modelForm, setModelForm] = React.useState({
    id: null,
    brandId: '',
    model: '',
    capacityGb: '',
    dateOfProduction: '',
    type: 'phone',     // NEW
    price: '',         // NEW (string pour TextField)
    phone: true,       // compat
    isActive: true,    // ✅ nouveau
  });
  const [componentForm, setComponentForm] = React.useState({ id: null, modelId: '', component: '', unitPrice: '' });

  /** ===================== LOAD ===================== */
  const loadAll = async () => {
    setErr(''); setOk('');
    try {
      const [b, m, c] = await Promise.all([
        api.get('/brands'),
        api.get('/models'),
        api.get('/components'),
      ]);
      setBrands(b.data || []);
      setModels(m.data || []);
      setComponents(c.data || []);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Chargement impossible.');
    }
  };
  React.useEffect(() => { loadAll(); }, []);

  /** ===================== DERIVÉS ===================== */
  const brand = brands.find(b => b.id === brandSel) || null;
  const model = models.find(m => m.id === modelSel) || null;

  const modelsFiltered = React.useMemo(() => {
    let list = brandSel ? models.filter(m => m.brandId === brandSel) : models;
    if (modelSearch.trim()) {
      const q = modelSearch.toLowerCase();
      list = list.filter(m => (m.model || '').toLowerCase().includes(q));
    }
    return list;
  }, [models, brandSel, modelSearch]);

  const componentsFiltered = modelSel ? components.filter(c => c.modelId === modelSel) : components;

  // comptages utiles pour les barres sélectionnées
  const modelsCountForBrand = brandSel ? models.filter(m => m.brandId === brandSel).length : 0;
  const componentsCountForModel = modelSel ? components.filter(c => c.modelId === modelSel).length : 0;

  /** ===================== HELPERS UI ===================== */
  const toggle = (section) => setExpanded(prev => (prev === section ? null : section));

  const selectBrand = (b) => {
    setBrandSel(b.id);
    setModelSel(null);
    setModelSearch('');
    setExpanded(null);
    setAddingBrand(false);
    setAddingModel(false);
    setAddingComponent(false);
    setModelForm(f => ({
      ...f,
      brandId: b.id,
      id: null,
      model: '',
      capacityGb: '',
      dateOfProduction: '',
      type: 'phone',
      price: '',
      phone: true,
    }));
    setTimeout(() => setExpanded('models'), 0);
  };

  const selectModel = (m) => {
    setModelSel(m.id);
    setExpanded(null);
    setAddingModel(false);
    setAddingComponent(false);
    setComponentForm(f => ({ ...f, modelId: m.id, id: null, component: '', unitPrice: '' }));
    setModelForm({
      id: m.id,
      brandId: m.brandId,
      model: m.model || '',
      capacityGb: m.capacityGb ?? '',
      dateOfProduction: m.dateOfProduction ?? '',
      type: m.type || 'phone',
      price: (m.price ?? 0).toString(),
      phone: !!m.phone,
      isActive: m.isActive === false ? false : true,   // ✅ par défaut actif si null/undefined
    });
    setTimeout(() => setExpanded('components'), 0);
  };

  const clearBrand = () => { setBrandSel(null); setModelSel(null); setExpanded('brands'); };
  const clearModel = () => { setModelSel(null); setExpanded('models'); };

  /** ===================== CRUD: BRANDS ===================== */
  const startAddBrand = () => {
    setAddingBrand(true);
    setBrandForm({
      id: null,
      brand: '',
      hasPricingInfo: false,
      pricingPrice: '',
      pricingDescription: '',
      isActive: true, // ✅ nouvelle marque active
    });
    setExpanded('brands');
  };
  const startEditBrand = (row) => {
    setAddingBrand(true);
    setBrandForm({
      id: row.id,
      brand: row.brand || '',
      hasPricingInfo: !!row.hasPricingInfo,
      pricingPrice: row.pricingPrice != null ? String(row.pricingPrice) : '',
      pricingDescription: row.pricingDescription || '',
      isActive: row.isActive === false ? false : true, // ✅ default true si null
    });
    setExpanded('brands');
  };
  const saveBrand = async () => {
    try {
      const name = (brandForm.brand || '').trim();
      if (!name) return setErr('Nom de marque requis.');

      // Normalise les champs pricing
      const payload = {
        brand: name,
        hasPricingInfo: !!brandForm.hasPricingInfo,
        isActive: brandForm.isActive === false ? false : true, // ✅
      };

      if (payload.hasPricingInfo) {
        let price = brandForm.pricingPrice === '' ? null : Number(brandForm.pricingPrice);
        if (price != null && !Number.isFinite(price)) {
          return setErr('Le prix de la marque doit être un nombre.');
        }
        if (price != null && price < 0) {
          return setErr('Le prix de la marque doit être positif.');
        }
        payload.pricingPrice = price; // peut rester null
        payload.pricingDescription = (brandForm.pricingDescription || '').trim();
      }

      if (brandForm.id) {
        await api.patch(`/brands/${brandForm.id}`, payload);
        setOk('Marque mise à jour.');
      } else {
        const { data } = await api.post('/brands', payload);
        setOk('Marque créée.');
        selectBrand(data);
      }
      await loadAll();
      setAddingBrand(false);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Échec marque.');
    }
  };
  const deleteBrand = async (id) => {
    if (!window.confirm('Supprimer cette marque ?')) return;
    try { await api.delete(`/brands/${id}`); setOk('Marque supprimée.'); clearBrand(); await loadAll(); }
    catch (e) { setErr(e?.response?.data?.message || 'Suppression impossible.'); }
  };

  const toggleBrandActive = async (row) => {
    try {
      const next = row.isActive === false ? true : false;
      await api.patch(`/brands/${row.id}`, { isActive: next });
      setBrands(prev =>
        prev.map(b => (b.id === row.id ? { ...b, isActive: next } : b))
      );
      setOk(next ? 'Marque réactivée.' : 'Marque désactivée.');
    } catch (e) {
      setErr(e?.response?.data?.message || 'Impossible de changer le statut de la marque.');
    }
  };

  /** ===================== CRUD: MODELS ===================== */
  const startAddModel = () => {
    if (!brandSel) return;
    setAddingModel(true);
    setModelForm({
      id: null,
      brandId: brandSel,
      model: '',
      capacityGb: '',
      dateOfProduction: '',
      type: 'phone',
      price: '',
      phone: true,
      isActive: true,        // ✅ nouveau
    });
    setExpanded('models');
  };
  const startEditModel = (row) => {
    setAddingModel(true);
    setModelForm({
      id: row.id,
      brandId: row.brandId,
      model: row.model || '',
      capacityGb: row.capacityGb ?? '',
      dateOfProduction: row.dateOfProduction ?? '',
      type: row.type || 'phone',
      price: (row.price ?? 0).toString(),
      phone: !!row.phone,
      isActive: row.isActive === false ? false : true,   // ✅
    });
    setExpanded('models');
  };

  const saveModel = async () => {
    try {
      if (!modelForm.brandId) return setErr('Choisis une marque.');
      if (!String(modelForm.model).trim()) return setErr('Nom du modèle requis.');

      // normalise le prix (entier >= 0)
      const parsedPrice = modelForm.price === '' ? 0 : Number(modelForm.price);
      if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
        return setErr('Le prix doit être un nombre positif.');
      }

      // si type=phone mais phone=false => on force type/phone cohérents
      const payload = {
        brandId: modelForm.brandId,
        model: String(modelForm.model).trim(),
        capacityGb: modelForm.capacityGb || null,
        dateOfProduction: modelForm.dateOfProduction || null,
        type: modelForm.type || 'phone',
        price: Math.round(parsedPrice),
        phone: !!modelForm.phone,
        isActive: modelForm.isActive === false ? false : true,   // ✅
      };

      if (modelForm.id) {
        await api.patch(`/models/${modelForm.id}`, payload);
        setOk('Modèle mis à jour.');
      } else {
        const { data } = await api.post('/models', payload);
        setOk('Modèle créé.');
        selectModel(data);
      }
      await loadAll();
      setAddingModel(false);
    } catch (e) { setErr(e?.response?.data?.message || 'Échec modèle.'); }
  };

  const deleteModel = async (id) => {
    if (!window.confirm('Supprimer ce modèle ?')) return;
    try { await api.delete(`/models/${id}`); setOk('Modèle supprimé.'); clearModel(); await loadAll(); }
    catch (e) { setErr(e?.response?.data?.message || 'Suppression impossible.'); }
  };

  /** ===================== CRUD: COMPONENTS ===================== */
  const startAddComponent = () => {
    if (!modelSel) return;
    setAddingComponent(true);
    setComponentForm({ id: null, modelId: modelSel, component: '', unitPrice: '' });
    setExpanded('components');
  };
  const startEditComponent = (row) => {
    setAddingComponent(true);
    setComponentForm({
      id: row.id,
      modelId: row.modelId,
      component: row.component || '',
      unitPrice: row.unitPrice ?? '',
    });
    setExpanded('components');
  };
  const saveComponent = async () => {
    try {
      if (!componentForm.modelId) return setErr('Choisis un modèle.');
      if (!String(componentForm.component).trim()) return setErr('Nom du composant requis.');
      const payload = {
        modelId: componentForm.modelId,
        component: String(componentForm.component).trim(),
        unitPrice: componentForm.unitPrice === '' ? 0 : Number(componentForm.unitPrice),
      };
      if (Number.isNaN(payload.unitPrice)) return setErr('Le prix doit être numérique.');
      if (componentForm.id) {
        await api.patch(`/components/${componentForm.id}`, payload);
        setOk('Composant mis à jour.');
      } else {
        await api.post('/components', payload);
        setOk('Composant créé.');
      }
      await loadAll();
      setAddingComponent(false);
    } catch (e) { setErr(e?.response?.data?.message || 'Échec composant.'); }
  };
  const deleteComponent = async (id) => {
    if (!window.confirm('Supprimer ce composant ?')) return;
    try { await api.delete(`/components/${id}`); setOk('Composant supprimé.'); await loadAll(); }
    catch (e) { setErr(e?.response?.data?.message || 'Suppression impossible.'); }
  };

  /** ===================== RENDER: WRAPPERS ===================== */
  const SectionHeader = ({ icon, title, isOpen, onToggle, trailing }) => (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 1, cursor: 'pointer', '&:hover': { bgcolor: '#f7f9ff' } }}
      onClick={onToggle}
    >
      <Stack direction="row" alignItems="center" spacing={1.2}>
        {icon}
        <Typography sx={{ fontWeight: 900 }}>{title}</Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        {trailing}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        >
          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Stack>
    </Stack>
  );

  // Ligne sélectionnée — pleine largeur + bordure bleue + détails
  const SelectedRow = ({ label, chips = [], meta = null, onClick }) => (
    <List dense>
      <ListItemButton
        onClick={onClick}
        sx={{
          borderRadius: 1,
          px: 1,
          border: `2px solid ${BLUE}`,
          bgcolor: 'rgba(11,95,255,.06)',
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontWeight: 800, color: BLUE }} noWrap>{label}</Typography>
              {chips.map((c, i) => (
                <Chip key={i} label={c} size="small" sx={{ height: 22, '& .MuiChip-label': { px: 1 } }} />
              ))}
            </Box>
          }
          secondary={meta}
          secondaryTypographyProps={{ component: 'div' }}
        />
        <Tooltip title="Réouvrir">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onClick(); }}>
            <ExpandMoreIcon fontSize="small" sx={{ color: BLUE }} />
          </IconButton>
        </Tooltip>
      </ListItemButton>
    </List>
  );

  const Empty = ({ label }) => (
    <Box sx={{ px: 1.5, py: 2 }}>
      <Muted>{label}</Muted>
    </Box>
  );

  const itemSx = {
    borderRadius: 1,
    px: 1,
    '& .MuiIconButton-root': { opacity: 0.6 },
    '&:hover .MuiIconButton-root': { opacity: 1 },
  };

  /** ===================== UI ===================== */
  return (
    <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 2 }}>{ok}</Alert>}

      {/* ====== MARQUES ====== */}
      <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, mb: 2 }}>
        <SectionHeader
          icon={<CategoryOutlinedIcon fontSize="small" />}
          title="Marques"
          isOpen={expanded === 'brands'}
          onToggle={() => toggle('brands')}
          trailing={
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setAddingBrand(!addingBrand);
                setBrandForm({ id: null, brand: '' });
                setExpanded('brands');
              }}
              startIcon={<AddRoundedIcon />}
              size="small"
              sx={{ color: '#777', textTransform: 'none', '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' } }}
            >
              Nouveau
            </Button>
          }
        />

        {/* Sélection (repliée) — afficher le nombre de modèles */}
        {brandSel && expanded !== 'brands' && (
          <SelectedRow
            label={brand?.brand}
            chips={[
              `${modelsCountForBrand} modèle${modelsCountForBrand > 1 ? 's' : ''}`,
              brand?.hasPricingInfo ? `Prix: ${brand?.pricingPrice != null ? Number(brand.pricingPrice).toFixed(2) + ' €' : '—'}` : 'Sans prix marque',
            ]}
            meta={brand?.hasPricingInfo && brand?.pricingDescription ? (
              <Muted>
{'\n'}{brand.pricingDescription}{'\n'}
              </Muted>
            ) : null}
            onClick={() => { clearBrand(); setExpanded('brands'); }}
          />
        )}

        <Collapse in={expanded === 'brands'} unmountOnExit>
          {/* Ligne “+ nouveau” inline */}
          <Collapse in={addingBrand} unmountOnExit>
            <Box sx={{ ...subtleCell, p: 1, mb: 1 }}>
              <Stack spacing={1}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <TextField
                    size="small"
                    label="Nom de la marque"
                    value={brandForm.brand}
                    onChange={(e) => setBrandForm(f => ({ ...f, brand: e.target.value }))}
                    sx={{ ...textFieldSx, flex: 1 }}
                  />

                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!brandForm.hasPricingInfo}
                          onChange={(e) => setBrandForm(f => ({ ...f, hasPricingInfo: e.target.checked }))}
                        />
                      }
                      label={<Muted>Afficher prix & description</Muted>}
                    />
                    {/* ✅ nouvelle bascule active / inactive */}
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={brandForm.isActive !== false}
                          onChange={(e) =>
                            setBrandForm(f => ({ ...f, isActive: e.target.checked }))
                          }
                        />
                      }
                      label={<Muted>Marque active</Muted>}
                    />
                  </Stack>
                </Stack>

                {/* Champs conditionnels */}
                <Collapse in={!!brandForm.hasPricingInfo} unmountOnExit>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }} sx={{ mt: 0.5 }}>
                    <TextField
                      size="small"
                      label="Prix (€)"
                      value={brandForm.pricingPrice}
                      onChange={(e) => setBrandForm(f => ({ ...f, pricingPrice: e.target.value }))}
                      InputProps={{ inputMode: 'numeric', endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                      sx={{ ...textFieldSx, width: { xs: '100%', sm: 220 } }}
                    />
                    <TextField
                      size="small"
                      label="Description"
                      value={brandForm.pricingDescription}
                      onChange={(e) => setBrandForm(f => ({ ...f, pricingDescription: e.target.value }))}
                      multiline
                      minRows={2}
                      sx={{ ...textFieldSx, flex: 1 }}
                    />
                  </Stack>
                </Collapse>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" onClick={saveBrand} sx={blueBtn} disabled={!brandForm.brand.trim()}>
                    {brandForm.id ? 'Enregistrer' : 'Créer'}
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Collapse>

          {brands.length === 0 ? (
            <Empty label="Aucune marque pour le moment." />
          ) : (
            <List dense>
              {brands.map((b, idx) => (
                <React.Fragment key={b.id}>
                <ListItemButton
                  onClick={() => selectBrand(b)}
                  sx={{
                    ...itemSx,
                    opacity: b.isActive === false ? 0.6 : 1,
                    bgcolor: b.isActive === false ? '#fafafa' : undefined,
                  }}
                >
                  <ListItemText
                    primary={
                      <Box>
                        {b.hasPricingInfo && (
                          <Box sx={{ mb: 0.25 }}>
                            <Typography sx={{ fontWeight: 700 }}>
                              {b.pricingPrice != null ? `${Number(b.pricingPrice).toFixed(2)} €` : '— €'}
                            </Typography>
                            {b.pricingDescription && (
                              <Typography sx={{ color: 'text.secondary', fontSize: 12, lineHeight: 1.4 }} noWrap>
                                {b.pricingDescription}
                              </Typography>
                            )}
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography sx={{ fontWeight: 700 }} noWrap>
                            {b.brand}
                          </Typography>
                          {/* ✅ petit chip état */}
                          <Chip
                            size="small"
                            label={b.isActive === false ? 'Inactif' : 'Actif'}
                            color={b.isActive === false ? 'default' : 'success'}
                            variant={b.isActive === false ? 'outlined' : 'filled'}
                            sx={{ height: 22, '& .MuiChip-label': { px: 1 } }}
                          />
                        </Box>
                      </Box>
                    }
                  />
                  {/* ✅ switch rapide actif / inactif */}
                  <FormControlLabel
                    onClick={(e) => e.stopPropagation()}
                    control={
                      <Switch
                        size="small"
                        checked={b.isActive !== false}
                        onChange={() => toggleBrandActive(b)}
                      />
                    }
                    label={<Muted>Active</Muted>}
                    sx={{ mr: 1 }}
                  />
                  <Tooltip title="Modifier">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditBrand(b);
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Supprimer">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteBrand(b.id);
                      }}
                    >
                    </IconButton>
                  </Tooltip>
                </ListItemButton>
                  {idx < brands.length - 1 && <Divider component="li" sx={{ my: 0.25 }} />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Collapse>
      </Paper>

      {/* ====== MODÈLES ====== */}
      <Paper
        variant="outlined"
        sx={{ p: 1.25, borderRadius: 2, mb: 2, opacity: brandSel ? 1 : 0.6, pointerEvents: brandSel ? 'auto' : 'none' }}
      >
        <SectionHeader
          icon={<PrecisionManufacturingOutlinedIcon fontSize="small" />}
          title="Modèles"
          isOpen={expanded === 'models'}
          onToggle={() => toggle('models')}
          trailing={
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setAddingModel(true);
                setModelForm({
                  id: null,
                  brandId: brandSel || '',
                  model: '',
                  capacityGb: '',
                  dateOfProduction: '',
                  type: 'phone',
                  price: '',
                  phone: true,
                  isActive: true,
                });
                setExpanded('models');
              }}
              startIcon={<AddRoundedIcon />}
              size="small"
              disabled={!brandSel}
              sx={{ color: '#777', textTransform: 'none', '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' } }}
            >
              Nouveau
            </Button>
          }
        />

        {/* Barre de recherche */}
        <Box sx={{ px: 1, pt: 0.5, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Rechercher un modèle..."
            value={modelSearch}
            onChange={(e) => setModelSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={textFieldSx}
          />
        </Box>

        {/* Sélection (repliée) — afficher toutes les infos liées (marque, capacité, type, prix, produit) */}
        {modelSel && expanded !== 'models' && (
          <SelectedRow
            label={model?.model}
            chips={[
              brand ? `Marque: ${brand.brand}` : 'Marque: —',
              `Capacité: ${model?.capacityGb ?? '—'} GB`,
              `Type: ${model?.type || 'phone'}`,
              `Prix: ${(model?.price ?? 0)} €`,
              model?.phone ? 'Produit: oui' : 'Produit: non',
              model?.isActive === false ? 'Actif: non' : 'Actif: oui',
              `${componentsCountForModel} composant${componentsCountForModel > 1 ? 's' : ''}`,
            ]}
            meta={null}
            onClick={() => { clearModel(); setExpanded('models'); }}
          />
        )}

        <Collapse in={expanded === 'models'} unmountOnExit>
          {/* Ligne “+ nouveau” inline (création uniquement) */}
          <Collapse in={addingModel && !modelForm.id} unmountOnExit>
            <Box
              sx={{
                ...subtleCell, p: 1, mb: 1,
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1fr auto',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <TextField
                size="small"
                label="Nom"
                value={modelForm.model}
                onChange={(e) => setModelForm(f => ({ ...f, model: e.target.value }))}
                sx={textFieldSx}
              />
              <TextField
                size="small"
                label="Capacité (GB)"
                value={modelForm.capacityGb}
                onChange={(e) => setModelForm(f => ({ ...f, capacityGb: e.target.value }))}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                sx={textFieldSx}
              />
              <TextField
                size="small"
                select
                label="Type"
                value={modelForm.type}
                onChange={(e) => setModelForm(f => ({
                  ...f,
                  type: e.target.value,
                  phone: e.target.value === 'phone' ? true : f.phone,
                }))}
                sx={textFieldSx}
              >
                {MODEL_TYPES.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Prix (€)"
                value={modelForm.price}
                onChange={(e) => setModelForm(f => ({ ...f, price: e.target.value }))}
                InputProps={{ inputMode: 'numeric', endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                sx={textFieldSx}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', ml: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={!!modelForm.phone}
                      onChange={(e) => setModelForm(f => ({ ...f, phone: e.target.checked }))}
                    />
                  }
                  label={<Muted>Téléphone vente</Muted>}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={modelForm.isActive !== false}
                      onChange={(e) => setModelForm(f => ({ ...f, isActive: e.target.checked }))}
                    />
                  }
                  label={<Muted>Actif (visible PriceList)</Muted>}
                />
              </Box>
              <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={saveModel}
                  sx={blueBtn}
                  disabled={!brandSel || !String(modelForm.model).trim()}
                >
                  {modelForm.id ? 'Enregistrer' : 'Créer'}
                </Button>
              </Box>
            </Box>
          </Collapse>

          {modelsFiltered.length === 0 ? (
            <Empty label={brandSel ? 'Aucun modèle pour cette marque.' : 'Sélectionne une marque pour voir les modèles.'} />
          ) : (
            <List dense>
              {modelsFiltered.map((m, idx) => (
                <React.Fragment key={m.id}>
                  <ListItemButton
                    onClick={() => selectModel(m)}
                    sx={{
                      ...itemSx,
                      opacity: m.isActive === false ? 0.5 : 1,
                      bgcolor: m.isActive === false ? '#fafafa' : undefined,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            minWidth: 0,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 800 }} noWrap>
                              {m.model}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.25 }}>
                              <Muted noWrap>
                                Capacité: {m.capacityGb ?? '—'} GB
                              </Muted>
                              <Muted noWrap>
                                Type: {m.type || 'phone'}
                              </Muted>
                            </Box>
                          </Box>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{ flexShrink: 0, alignItems: 'center' }}
                          >
                            <Chip
                              size="small"
                              label={`Prix ${(m.price ?? 0)} €`}
                              sx={{ height: 22, '& .MuiChip-label': { px: 1 } }}
                            />
                            <Chip
                              size="small"
                              label={m.phone ? 'Produit' : 'Hors catalogue'}
                              variant={m.phone ? 'outlined' : 'filled'}
                              color={m.phone ? 'default' : 'warning'}
                              sx={{ height: 22, '& .MuiChip-label': { px: 1 } }}
                            />
                            <Chip
                              size="small"
                              label={m.isActive === false ? 'Inactif' : 'Actif'}
                              color={m.isActive === false ? 'default' : 'success'}
                              variant={m.isActive === false ? 'outlined' : 'filled'}
                              sx={{ height: 22, '& .MuiChip-label': { px: 1 } }}
                            />
                          </Stack>
                        </Box>
                      }
                    />
                    <Tooltip title="Modifier">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditModel(m); // va remplir modelForm + addingModel = true
                        }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteModel(m.id); }}>
                      </IconButton>
                    </Tooltip>
                  </ListItemButton>

                  {/* Formulaire inline au niveau du modèle quand on édite */}
                  <Collapse in={addingModel && !!modelForm.id && modelForm.id === m.id} unmountOnExit>
                    <Box
                      sx={{
                        ...subtleCell, p: 1, mb: 1, mt: 0.5,
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr 1fr auto',
                        gap: 1,
                        alignItems: 'center',
                      }}
                    >
                      <TextField
                        size="small"
                        label="Nom"
                        value={modelForm.model}
                        onChange={(e) => setModelForm(f => ({ ...f, model: e.target.value }))}
                        sx={textFieldSx}
                      />
                      <TextField
                        size="small"
                        label="Capacité (GB)"
                        value={modelForm.capacityGb}
                        onChange={(e) => setModelForm(f => ({ ...f, capacityGb: e.target.value }))}
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        sx={textFieldSx}
                      />
                      <TextField
                        size="small"
                        select
                        label="Type"
                        value={modelForm.type}
                        onChange={(e) => setModelForm(f => ({
                          ...f,
                          type: e.target.value,
                          phone: e.target.value === 'phone' ? true : f.phone,
                        }))}
                        sx={textFieldSx}
                      >
                        {MODEL_TYPES.map(t => (
                          <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        size="small"
                        label="Prix (€)"
                        value={modelForm.price}
                        onChange={(e) => setModelForm(f => ({ ...f, price: e.target.value }))}
                        InputProps={{ inputMode: 'numeric', endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                        sx={textFieldSx}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', ml: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={!!modelForm.phone}
                              onChange={(e) => setModelForm(f => ({ ...f, phone: e.target.checked }))}
                            />
                          }
                          label={<Muted>Téléphone vente</Muted>}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={modelForm.isActive !== false}
                              onChange={(e) => setModelForm(f => ({ ...f, isActive: e.target.checked }))}
                            />
                          }
                          label={<Muted>Actif (visible PriceList)</Muted>}
                        />
                      </Box>
                      <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          onClick={saveModel}
                          sx={blueBtn}
                          disabled={!brandSel || !String(modelForm.model).trim()}
                        >
                          Enregistrer
                        </Button>
                      </Box>
                    </Box>
                  </Collapse>

                  {idx < modelsFiltered.length - 1 && <Divider component="li" sx={{ my: 0.25 }} />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Collapse>
      </Paper>


      {/* ====== COMPOSANTS ====== */}
      <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, opacity: modelSel ? 1 : 0.6, pointerEvents: modelSel ? 'auto' : 'none' }}>
        <SectionHeader
          icon={<Inventory2OutlinedIcon fontSize="small" />}
          title="Composants"
          isOpen={expanded === 'components'}
          onToggle={() => toggle('components')}
          trailing={
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setAddingComponent(!addingComponent);
                setComponentForm({ id: null, modelId: modelSel || '', component: '', unitPrice: '' });
                setExpanded('components');
              }}
              startIcon={<AddRoundedIcon />}
              size="small"
              disabled={!modelSel}
              sx={{ color: '#777', textTransform: 'none', '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' } }}
            >
              Nouveau
            </Button>
          }
        />

        {/* Sélection (repliée) — affiche aussi les infos du modèle */}
        {expanded !== 'components' && modelSel && (
          <SelectedRow
            label={model?.model}
            chips={[
              brand ? `Marque: ${brand.brand}` : 'Marque: —',
              `Capacité: ${model?.capacityGb ?? '—'} GB`,
              `Type: ${model?.type || 'phone'}`,
              `Prix: ${(model?.price ?? 0)} €`,
              model?.phone ? 'Produit: oui' : 'Produit: non',
              `${componentsCountForModel} composant${componentsCountForModel > 1 ? 's' : ''}`,
            ]}
            meta={null}
            onClick={() => { setExpanded('components'); }}
          />
        )}

        <Collapse in={expanded === 'components'} unmountOnExit>
          {/* Ligne “+ nouveau” inline */}
          <Collapse in={addingComponent} unmountOnExit>
            <Box
              sx={{
                ...subtleCell, p: 1, mb: 1,
                display: 'grid',
                gridTemplateColumns: '2fr 1fr auto',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <TextField
                size="small"
                label="Nom du composant"
                value={componentForm.component}
                onChange={(e) => setComponentForm(f => ({ ...f, component: e.target.value }))}
                sx={textFieldSx}
              />
              <TextField
                size="small"
                label="Prix unitaire"
                value={componentForm.unitPrice}
                onChange={(e) => setComponentForm(f => ({ ...f, unitPrice: e.target.value }))}
                InputProps={{ inputMode: 'decimal', endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                sx={textFieldSx}
              />
              <Button
                variant="contained"
                onClick={saveComponent}
                sx={blueBtn}
                disabled={!modelSel || !String(componentForm.component).trim()}
              >
                {componentForm.id ? 'Enregistrer' : 'Créer'}
              </Button>
            </Box>
          </Collapse>

          {componentsFiltered.length === 0 ? (
            <Empty label={modelSel ? 'Aucun composant pour ce modèle.' : 'Sélectionne un modèle pour voir les composants.'} />
          ) : (
            <List dense>
              {componentsFiltered.map((c, idx) => (
                <React.Fragment key={c.id}>
                  <ListItemButton sx={itemSx}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontWeight: 800 }} noWrap>{c.component}</Typography>
                          <Muted noWrap>• {c.unitPrice ?? 0} €</Muted>
                        </Box>
                      }
                    />
                    <Tooltip title="Modifier">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); startEditComponent(c); }}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteComponent(c.id); }}>
                      </IconButton>
                    </Tooltip>
                  </ListItemButton>
                  {idx < componentsFiltered.length - 1 && <Divider component="li" sx={{ my: 0.25 }} />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Collapse>
      </Paper>
    </Paper>
  );
}